import { Button, Input, Space, Table, Tabs, message } from 'antd';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState } from '../../components/EmptyState';
import { RejectDrawer } from '../../components/RejectDrawer';
import { StatusBadge } from '../../components/StatusBadge';
import { ApiError } from '../../lib/http';
import { rejectReasonLabel } from '../../lib/status';
import { reservationsApi } from '../../services/resources';
import type { RejectReasonType, Reservation, ReservationStatus } from '../../types/api';

const TABS: { key: string; label: string }[] = [
  { key: 'pending', label: '待审核' },
  { key: 'approved', label: '已通过' },
  { key: 'rejected', label: '已驳回' },
  { key: 'cancelled', label: '已取消' },
  { key: 'expired', label: '已过期' },
  { key: 'completed', label: '已完成' },
  { key: 'all', label: '全部' },
];

export function AdminReservationsPage() {
  const [tab, setTab] = useState('pending');
  const [keyword, setKeyword] = useState('');
  const [items, setItems] = useState<Reservation[]>([]);
  const [error, setError] = useState('');
  const [target, setTarget] = useState<Reservation | null>(null);

  async function load() {
    try {
      const q: Record<string, string | number> = { page: 1, limit: 50 };
      if (tab !== 'all') q.status = tab;
      const page = await reservationsApi.list(q);
      const filtered = keyword
        ? page.items.filter((r) => `${r.user_name ?? ''}${r.remark ?? ''}`.includes(keyword))
        : page.items;
      setItems(filtered);
      setError('');
    } catch (e) {
      setError(e instanceof ApiError ? `列表加载失败：${e.message}` : '列表加载失败，请检查网络后重试');
    }
  }

  useEffect(() => {
    void load();
    // keyword 由筛选按钮触发，不随输入即时打接口
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  async function approve(row: Reservation) {
    try {
      await reservationsApi.approve(row.id);
      await load();
    } catch (e) {
      if (e instanceof ApiError && e.code === 40909) {
        message.error('该时段已占用');
      } else {
        message.error(e instanceof ApiError ? e.message : '操作失败');
      }
    }
  }

  async function reject(reason_code: RejectReasonType, detail: string) {
    if (!target) return;
    await reservationsApi.reject(target.id, {
      reason: rejectReasonLabel(reason_code),
      comment: detail,
    });
    setTarget(null);
    await load();
  }

  return (
    <div className="stack">
      <h1 className="page-title">预约审核</h1>
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={TABS.map((t) => ({ key: t.key, label: t.label }))}
        className="scroll-tabs"
      />
      <Space>
        <Input
          className="touch"
          placeholder="关键词"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onPressEnter={() => void load()}
        />
        <Button className="touch" onClick={() => void load()}>
          筛选
        </Button>
      </Space>
      {error ? (
        <EmptyState text={error} action={{ label: '重新加载', onClick: () => void load() }} />
      ) : items.length === 0 ? (
        <EmptyState text="没有符合筛选的预约。" kind="search" />
      ) : (
        <Table
          scroll={{ x: 1000 }}
          rowKey="id"
          dataSource={items}
          pagination={false}
          columns={[
            { title: '申请人', dataIndex: 'user_name', width: 90, render: (v: string | null) => v || '—' },
            {
              title: '班级 / 教研组',
              dataIndex: 'class_or_group',
              width: 110,
              render: (v: string | null) => v || '—',
            },
            {
              title: '实验室',
              width: 130,
              render: (_, r) =>
                r.lab ? (
                  <span>
                    {r.lab.name}
                    <span className="mono meta"> {r.lab.room_code}</span>
                  </span>
                ) : (
                  <span className="mono meta">{r.lab_id}</span>
                ),
            },
            {
              title: '日期节次',
              width: 150,
              render: (_, r) => `${r.date} ${(r.period_names ?? []).join('、')}`,
            },
            { title: '事由', dataIndex: 'remark', width: 180 },
            {
              title: '状态',
              width: 100,
              render: (_, r) => <StatusBadge status={r.status as ReservationStatus} reasonType={r.reason_type} />,
            },
            {
              title: '操作',
              width: 140,
              render: (_, r) =>
                r.status === 'pending' ? (
                  <Space>
                    <Button className="touch" type="primary" onClick={() => void approve(r)}>
                      通过
                    </Button>
                    <Button className="touch" danger onClick={() => setTarget(r)}>
                      驳回
                    </Button>
                  </Space>
                ) : r.status === 'rejected' && !r.reject_reason && !r.comment ? (
                  <span className="meta">原因缺失，请联系实验员补充</span>
                ) : null,
            },
          ]}
        />
      )}
      {error.includes('占用') ? <Link to="/admin/labs">打开冲突日历</Link> : null}
      <RejectDrawer
        open={!!target}
        onClose={() => setTarget(null)}
        onSubmit={({ reason_code, reason_detail }) => reject(reason_code, reason_detail)}
      />
    </div>
  );
}
